// activeItemTray.ts
//
// BETA 16.38 — Bottom-left "active items" tray. Per Dylan: VooGoo Rager
// (and other Q-active items) should show as a persistent, always-visible
// chip in the bottom-left corner with cooldown sweep, hover tooltip, and
// activation flash.
//
// Data contract:
//   - An "active item" is any inventory slot whose item description starts
//     with "Active item (Q):" — covers VooGoo Rager (true Q-press),
//     Asmon's Desk Banana (auto-drop on cd), Whoopie Cushion of Doom
//     (auto-drop on cd). All three live in the tray with their actual
//     cooldown sourced live from engine.ts module timers.
//   - Tray slots are addressed by item-id so a re-pick animates only
//     that specific slot.
//
// Animations:
//   - pickAnim     — 0..1 over ~1.2s when the slot first appears.
//                    Renders a spin-and-scale-in at the tray position.
//                    (Dylan: "the card spins and goes towards the bottom
//                    left screen to live not covering any UI on screen.")
//   - activateFlashT — 0..1 over 0.6s when the item fires (Q-press for
//                      VooGoo, auto-drop for Banana/Whoopie).
//                      (Dylan: "if i activate it it should shake or
//                      something and disolve and actually do what it
//                      says it does in game.")

export interface ActiveItemSlotState {
  itemId: string;
  pickAnimT: number;        // 1 → 0 over PICK_ANIM_DURATION
  activateFlashT: number;   // 1 → 0 over ACTIVATE_FLASH_DURATION
}

const _slots: Map<string, ActiveItemSlotState> = new Map();

const PICK_ANIM_DURATION = 1.2;
const ACTIVATE_FLASH_DURATION = 0.6;

/** Reset on new run. */
export function resetActiveItemTray(): void {
  _slots.clear();
}

/** Called each tick. itemIds is the current list of active items in
 *  inventory order. New items get a pick animation; missing items get
 *  removed. Animations decay regardless. */
export function syncActiveItemSlots(itemIds: string[], dt: number): void {
  const seen = new Set(itemIds);
  // Add new
  for (const id of itemIds) {
    if (!_slots.has(id)) {
      _slots.set(id, {
        itemId: id,
        pickAnimT: 1.0,
        activateFlashT: 0,
      });
    }
  }
  // Remove vanished
  for (const id of Array.from(_slots.keys())) {
    if (!seen.has(id)) _slots.delete(id);
  }
  // Decay timers
  for (const slot of Array.from(_slots.values())) {
    if (slot.pickAnimT > 0) slot.pickAnimT = Math.max(0, slot.pickAnimT - dt / PICK_ANIM_DURATION);
    if (slot.activateFlashT > 0) slot.activateFlashT = Math.max(0, slot.activateFlashT - dt / ACTIVATE_FLASH_DURATION);
  }
}

/** Trigger the activation flash animation on a specific item. Called
 *  from engine.ts when Q fires (VooGoo) or auto-drop fires (Banana/
 *  Whoopie). No-op if the item isn't in the tray. */
export function flashActiveItem(itemId: string): void {
  const slot = _slots.get(itemId);
  if (slot) slot.activateFlashT = 1.0;
}

/** Get tray slots in display order (matches the order itemIds was passed
 *  in via syncActiveItemSlots). */
export function getActiveItemSlots(): ActiveItemSlotState[] {
  return Array.from(_slots.values());
}

/** Slot-box geometry (constant — render code mirrors). */
export const TRAY_SLOT_SIZE = 76;
export const TRAY_SLOT_GAP = 10;
export const TRAY_PAD_X = 18;
export const TRAY_PAD_BOTTOM = 90;  // distance from canvas bottom edge

/** Compute the screen-space bbox for slot `i` given canvas height. */
export function getTraySlotBbox(i: number, canvasH: number): { x: number; y: number; w: number; h: number } {
  return {
    x: TRAY_PAD_X + i * (TRAY_SLOT_SIZE + TRAY_SLOT_GAP),
    y: canvasH - TRAY_PAD_BOTTOM,
    w: TRAY_SLOT_SIZE,
    h: TRAY_SLOT_SIZE,
  };
}
